import React, { useState, useRef, useEffect, useMemo } from 'react';
import { buildForbiddenPatterns, isCommandForbidden } from '../../utilis/terminalSecurity';

const Terminal = ({ user, response, onCommandSubmit, connectionStabished, forbiddenCommands }) => {
  const [commands, setCommands] = useState([]);
  const [input, setInput] = useState('');
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [commandHistory, setCommandHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const terminalRef = useRef(null);

  // Compila lista final de padrões proibidos combinando padrão + prop opcional
  const forbiddenPatterns = useMemo(
    () => buildForbiddenPatterns(forbiddenCommands),
    [forbiddenCommands]
  );

  // Carregar histórico do localStorage na inicialização
  useEffect(() => {
    const savedHistory = localStorage.getItem('terminal-command-history');
    if (savedHistory) {
      try {
        const parsedHistory = JSON.parse(savedHistory);
        setCommandHistory(parsedHistory);
      } catch (error) {
        console.error('Erro ao carregar histórico de comandos:', error);
      }
    }
  }, []);

  // Salvar comando no histórico
  const saveCommandToHistory = (command) => {
    const newHistory = [...commandHistory];

    // Evitar comandos duplicados consecutivos
    if (newHistory[newHistory.length - 1] !== command) {
      newHistory.push(command);

      // Limitar histórico a 100 comandos
      if (newHistory.length > 100) {
        newHistory.shift();
      }

      setCommandHistory(newHistory);
      localStorage.setItem('terminal-command-history', JSON.stringify(newHistory));
    }

    // Resetar índice do histórico
    setHistoryIndex(-1);
  };

  // Navegação no histórico com setas
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (commandHistory.length > 0) {
        const newIndex = historyIndex === -1
          ? commandHistory.length - 1
          : Math.max(0, historyIndex - 1);
        setHistoryIndex(newIndex);
        setInput(commandHistory[newIndex]);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (historyIndex >= 0) {
        const newIndex = historyIndex + 1;
        if (newIndex >= commandHistory.length) {
          setHistoryIndex(-1);
          setInput('');
        } else {
          setHistoryIndex(newIndex);
          setInput(commandHistory[newIndex]);
        }
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim()) {
      const command = `> ${input}`;
      const trimmedInput = input.trim();

      // Adiciona o comando digitado no output
      setCommands((prevCommands) => [...prevCommands, { text: command, type: 'command' }]);

      // Se for proibido, não enviar e mostrar erro
      if (isCommandForbidden(trimmedInput, forbiddenPatterns)) {
        setCommands((prev) => [
          ...prev,
          { text: 'Comando bloqueado por segurança.', type: 'errorResponse' },
        ]);
        setInput('');
        setShouldAutoScroll(true);
        return; // não salva no histórico nem envia
      }

      // Salvar no histórico apenas se permitido
      saveCommandToHistory(trimmedInput);

      setInput('');
      // Força auto-scroll quando o usuário envia um comando
      setShouldAutoScroll(true);
      // Chama a função passada via props
      if (onCommandSubmit) {
        onCommandSubmit(trimmedInput);
      }
    }
  };

  // Função para verificar se o usuário está no final do scroll
  const isAtBottom = () => {
    if (!terminalRef.current) return true;
    const { scrollTop, scrollHeight, clientHeight } = terminalRef.current;
    return scrollHeight - scrollTop - clientHeight < 10; // 10px de tolerância
  };

  // Função para lidar com o scroll
  const handleScroll = () => {
    if (terminalRef.current) {
      setShouldAutoScroll(isAtBottom());
    }
  };

  useEffect(() => {
    if (shouldAutoScroll && terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [commands, shouldAutoScroll]);

  useEffect(() => {
    if (response) {
      const responseType = response.startsWith('comando:') ? 'highlightedResponse' : 'normalResponse';
      setCommands((prevCommands) => [...prevCommands, { text: response, type: responseType }]);
    }
  }, [response]);

  return (
    <div style={styles.terminalContainer}>
      <div
        ref={terminalRef}
        style={styles.output}
        onScroll={handleScroll}
      >
        {commands.map((command, index) => (
          <div
            key={index}
            style={
              command.type === 'command'
                ? styles.command
                : command.type === 'highlightedResponse'
                  ? styles.highlightedResponse
                  : command.type === 'errorResponse'
                    ? styles.errorResponse
                    : styles.normalResponse
            }
          >
            {command.text}
          </div>
        ))}
      </div>
      <form
        onSubmit={handleSubmit}
        style={styles.form}
      >
        <input
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Digite um comando..."
          style={styles.input}
          type="text"
          value={input}
          disabled={!user?.permission?.supportdry?.write}
        />
      </form>
    </div>
  );
};

const styles = {
  terminalContainer: {
    width: '100%',
    height: '100%',
    backgroundColor: '#1e1e1e',
    color: '#00ff00',
    borderRadius: '5px',
    padding: '10px',
    display: 'flex',
    flexDirection: 'column',
    fontSize: '14px',
  },
  output: {
    flex: 1,
    overflowY: 'auto',
    marginBottom: '10px',
    whiteSpace: 'pre-wrap', // Faz com que \n quebre a linha
  },
  command: {
    color: '#00ff00',
    margin: '2px 0',
  },
  highlightedResponse: {
    color: '#1E90FF',
    margin: '2px 0',
  },
  normalResponse: {
    color: '#FFFFFF',
    margin: '2px 0',
  },
  errorResponse: {
    color: '#FF5555',
    margin: '2px 0',
  },
  form: {
    display: 'flex',
  },
  input: {
    flex: 1,
    padding: '8px',
    borderRadius: '5px',
    border: '1px solid #555',
    backgroundColor: '#333',
    color: '#00ff00',
    outline: 'none',
  },
};

export default Terminal;
